/**
 * Metadata + lazy-loader registry — the runtime source of truth for which
 * lessons exist.
 *
 * Hand-rolled (rather than `import.meta.glob`) so the runtime contract is
 * portable to mobile (Metro / Hermes), where Vite's glob helper doesn't
 * exist. The cost is one extra entry per lesson; the gain is a code-split
 * dynamic import per MDX module so per-lesson chunks land in their own
 * bundle (spec §14: < 30 KB gz/lesson).
 *
 * Why duplicate metadata in the registry instead of reading it from
 * `frontmatter` exports? Because the lesson index (`/learn`) needs to
 * render every lesson's title + read time + checkFor *without* loading
 * any MDX content — and the MDX modules transitively import lesson
 * helpers (`PlayButton`, `Score`, `plans`), so loading a single MDX
 * module pulls in the heavy components by side effect. Metadata-only
 * means metadata-only.
 *
 * To keep the duplication honest, `runtime.test.ts` loads each lesson
 * via the loader and asserts the parsed front-matter matches the
 * registry's `metadata` entry. Drift fails at test time.
 */

import type { LessonMetadata, LessonModule } from "./runtime.js";

/**
 * One registry entry per authored lesson. `metadata` is the plain-data
 * face shown to the index; `loader` is the dynamic import that pulls
 * the MDX module on demand.
 *
 * Each `import("…")` is a literal so Vite recognizes it as a code-split
 * point — the MDX module + its transitive lesson-helper imports land
 * in their own per-lesson chunk.
 */
export interface LessonRegistryEntry {
  metadata: LessonMetadata;
  loader: () => Promise<unknown>;
}

const ENTRIES: ReadonlyArray<LessonRegistryEntry> = [
  {
    metadata: {
      nodeId: "diatonic-degrees-major",
      title: "Hearing the major scale by function",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "diatonic-degrees-major::1" },
        { itemId: "diatonic-degrees-major::5" },
        { itemId: "diatonic-degrees-major::7" },
      ],
    },
    loader: () => import("../content/diatonic-degrees-major.mdx"),
  },
  {
    metadata: {
      nodeId: "diatonic-degrees-minor",
      title: "Hearing the minor scale by function",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "diatonic-degrees-minor::1" },
        { itemId: "diatonic-degrees-minor::3" },
        { itemId: "diatonic-degrees-minor::7" },
      ],
    },
    loader: () => import("../content/diatonic-degrees-minor.mdx"),
  },
  {
    metadata: {
      nodeId: "chromatic-degrees-major",
      title: "Chromatic degrees in major — fi, le, te",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "chromatic-degrees-major::♯4" },
        { itemId: "chromatic-degrees-major::♭6" },
        { itemId: "chromatic-degrees-major::♭7" },
      ],
    },
    loader: () => import("../content/chromatic-degrees-major.mdx"),
  },
  {
    metadata: {
      nodeId: "diatonic-intervals-major",
      title: "Diatonic intervals in key context",
      estReadMinutes: 8,
      checkFor: [
        { itemId: "diatonic-intervals-major::P5" },
        { itemId: "diatonic-intervals-major::M3" },
        { itemId: "diatonic-intervals-major::m2" },
      ],
    },
    loader: () => import("../content/diatonic-intervals-major.mdx"),
  },
  {
    metadata: {
      nodeId: "triad-quality",
      title: "Triad quality — major, minor, diminished, augmented",
      estReadMinutes: 6,
      checkFor: [
        { itemId: "triad-quality::maj" },
        { itemId: "triad-quality::min" },
        { itemId: "triad-quality::dim" },
      ],
    },
    loader: () => import("../content/triad-quality.mdx"),
  },
  {
    metadata: {
      nodeId: "modes-major-family",
      title: "Modes of the major family — ionian, lydian, mixolydian",
      estReadMinutes: 8,
      checkFor: [
        { itemId: "modes-major-family::ionian" },
        { itemId: "modes-major-family::lydian" },
        { itemId: "modes-major-family::mixolydian" },
      ],
    },
    loader: () => import("../content/modes-major-family.mdx"),
  },
  {
    metadata: {
      nodeId: "modes-minor-family",
      title: "Modes of the minor family — dorian, phrygian, aeolian",
      estReadMinutes: 8,
      checkFor: [
        { itemId: "modes-minor-family::dorian" },
        { itemId: "modes-minor-family::phrygian" },
        { itemId: "modes-minor-family::aeolian" },
      ],
    },
    loader: () => import("../content/modes-minor-family.mdx"),
  },
  {
    metadata: {
      nodeId: "modes-locrian",
      title: "Locrian — the half-diminished mode",
      estReadMinutes: 5,
      checkFor: [{ itemId: "modes-locrian::locrian" }],
    },
    loader: () => import("../content/modes-locrian.mdx"),
  },
  {
    metadata: {
      nodeId: "extended-chords-9",
      title: "9th chords — maj9, min9, dom9",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "extended-chords-9::maj9" },
        { itemId: "extended-chords-9::min9" },
        { itemId: "extended-chords-9::dom9" },
      ],
    },
    loader: () => import("../content/extended-chords-9.mdx"),
  },
  {
    metadata: {
      nodeId: "extended-chords-11-13",
      title: "11th and 13th chords — maj11, min11, dom13",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "extended-chords-11-13::maj11" },
        { itemId: "extended-chords-11-13::min11" },
        { itemId: "extended-chords-11-13::dom13" },
      ],
    },
    loader: () => import("../content/extended-chords-11-13.mdx"),
  },
  {
    metadata: {
      nodeId: "altered-dominants",
      title: "Altered dominants — 7♭9, 7♯9, 7♯11, 7♭13",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "altered-dominants::dom7b9" },
        { itemId: "altered-dominants::dom7s11" },
        { itemId: "altered-dominants::dom7b13" },
      ],
    },
    loader: () => import("../content/altered-dominants.mdx"),
  },
  {
    metadata: {
      nodeId: "slash-chords",
      title: "Slash chords — bass note, separate identity",
      estReadMinutes: 6,
      checkFor: [
        { itemId: "slash-chords::G-over-B" },
        { itemId: "slash-chords::C-over-E" },
        { itemId: "slash-chords::F-over-G" },
      ],
    },
    loader: () => import("../content/slash-chords.mdx"),
  },
  {
    metadata: {
      nodeId: "rhythmic-dictation-simple",
      title: "Rhythmic dictation — simple meter (4/4)",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "rhythmic-dictation-simple::even-quarters" },
        { itemId: "rhythmic-dictation-simple::div-beat1" },
        { itemId: "rhythmic-dictation-simple::dotted-beat1" },
      ],
    },
    loader: () => import("../content/rhythmic-dictation-simple.mdx"),
  },
  {
    metadata: {
      nodeId: "melodic-dictation-narrow",
      title: "Melodic dictation — short, narrow range",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "melodic-dictation-narrow::1" },
        { itemId: "melodic-dictation-narrow::2" },
        { itemId: "melodic-dictation-narrow::3" },
      ],
    },
    loader: () => import("../content/melodic-dictation-narrow.mdx"),
  },
  {
    metadata: {
      nodeId: "melodic-dictation-medium",
      title: "Melodic dictation — medium length, wider range",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "melodic-dictation-medium::1" },
        { itemId: "melodic-dictation-medium::2" },
        { itemId: "melodic-dictation-medium::3" },
      ],
    },
    loader: () => import("../content/melodic-dictation-medium.mdx"),
  },
  {
    metadata: {
      nodeId: "melodic-dictation-chromatic",
      title: "Melodic dictation — chromatic neighbors (♯4, ♭6)",
      estReadMinutes: 8,
      checkFor: [
        { itemId: "melodic-dictation-chromatic::1" },
        { itemId: "melodic-dictation-chromatic::2" },
        { itemId: "melodic-dictation-chromatic::3" },
      ],
    },
    loader: () => import("../content/melodic-dictation-chromatic.mdx"),
  },
  {
    metadata: {
      nodeId: "seventh-chord-quality",
      title: "Seventh chords — maj7, min7, dom7, and the diminished pair",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "seventh-chord-quality::maj7" },
        { itemId: "seventh-chord-quality::min7" },
        { itemId: "seventh-chord-quality::dom7" },
      ],
    },
    loader: () => import("../content/seventh-chord-quality.mdx"),
  },
  {
    metadata: {
      nodeId: "triad-inversions",
      title: "Triad inversions — root position, first, second",
      estReadMinutes: 6,
      checkFor: [
        { itemId: "triad-inversions::maj::0" },
        { itemId: "triad-inversions::maj::1" },
        { itemId: "triad-inversions::maj::2" },
      ],
    },
    loader: () => import("../content/triad-inversions.mdx"),
  },
  {
    metadata: {
      nodeId: "diatonic-progressions",
      title: "Common diatonic progressions",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "diatonic-progressions::I-IV-V-I" },
        { itemId: "diatonic-progressions::I-V-vi-IV" },
        { itemId: "diatonic-progressions::ii-V-I" },
      ],
    },
    loader: () => import("../content/diatonic-progressions.mdx"),
  },
  {
    metadata: {
      nodeId: "cadence-recognition",
      title: "Cadences — authentic, plagal, half, deceptive",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "cadence-recognition::PAC" },
        { itemId: "cadence-recognition::plagal" },
        { itemId: "cadence-recognition::half" },
      ],
    },
    loader: () => import("../content/cadence-recognition.mdx"),
  },
  {
    metadata: {
      nodeId: "secondary-dominant-recognition",
      title: "Secondary dominants — tonicizing within the key",
      estReadMinutes: 8,
      checkFor: [
        { itemId: "secondary-dominant-recognition::V/V" },
        { itemId: "secondary-dominant-recognition::V/vi" },
        { itemId: "secondary-dominant-recognition::V/ii" },
      ],
    },
    loader: () => import("../content/secondary-dominant-recognition.mdx"),
  },
  {
    metadata: {
      nodeId: "modulation-recognition",
      title: "Modulation — hearing the key move",
      estReadMinutes: 8,
      checkFor: [
        { itemId: "modulation-recognition::relative" },
        { itemId: "modulation-recognition::parallel" },
        { itemId: "modulation-recognition::dominant" },
      ],
    },
    loader: () => import("../content/modulation-recognition.mdx"),
  },
  {
    metadata: {
      nodeId: "chromatic-intervals",
      title: "Chromatic intervals — the tritone and its neighbors",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "chromatic-intervals::A4" },
        { itemId: "chromatic-intervals::d5" },
        { itemId: "chromatic-intervals::A2" },
      ],
    },
    loader: () => import("../content/chromatic-intervals.mdx"),
  },
  {
    metadata: {
      nodeId: "rhythmic-dictation-compound",
      title: "Rhythmic dictation — compound meter (6/8)",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "rhythmic-dictation-compound::even-dotted" },
        { itemId: "rhythmic-dictation-compound::div-beat1" },
        { itemId: "rhythmic-dictation-compound::all-eighths" },
      ],
    },
    loader: () => import("../content/rhythmic-dictation-compound.mdx"),
  },
  {
    metadata: {
      nodeId: "rhythmic-dictation-syncopation",
      title: "Rhythmic dictation — syncopation",
      estReadMinutes: 7,
      checkFor: [
        { itemId: "rhythmic-dictation-syncopation::synco-and1" },
        { itemId: "rhythmic-dictation-syncopation::offbeat-pairs" },
        { itemId: "rhythmic-dictation-syncopation::backbeat" },
      ],
    },
    loader: () => import("../content/rhythmic-dictation-syncopation.mdx"),
  },
  // ── Guitar track lessons ─────────────────────────────────────────────────
  // These lessons have checkFor: [] because fretboard exercises require a
  // native fretboard UI — not compatible with the web MiniExercise component.
  // Lesson completion is tracked via "Mark as read" on mobile and
  // the lesson page footer on web.
  {
    metadata: {
      nodeId: "fret-scale-degrees-major",
      title: "Scale degrees on the neck (major)",
      estReadMinutes: 5,
      checkFor: [],
    },
    loader: () => import("../content/fret-scale-degrees-major.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-caged-shapes-ea",
      title: "CAGED shapes — E and A",
      estReadMinutes: 6,
      checkFor: [],
    },
    loader: () => import("../content/fret-caged-shapes-ea.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-caged-shapes-cgd",
      title: "CAGED shapes — C, G, and D",
      estReadMinutes: 7,
      checkFor: [],
    },
    loader: () => import("../content/fret-caged-shapes-cgd.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-caged-anchoring",
      title: "CAGED anchoring — finding the root without a label",
      estReadMinutes: 5,
      checkFor: [],
    },
    loader: () => import("../content/fret-caged-anchoring.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-scale-degrees-caged",
      title: "Scale degrees in CAGED shapes",
      estReadMinutes: 5,
      checkFor: [],
    },
    loader: () => import("../content/fret-scale-degrees-caged.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-diatonic-triads",
      title: "Diatonic triads on the neck",
      estReadMinutes: 6,
      checkFor: [],
    },
    loader: () => import("../content/fret-diatonic-triads.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-diatonic-triads-minor",
      title: "Minor key diatonic triads on the neck",
      estReadMinutes: 6,
      checkFor: [],
    },
    loader: () => import("../content/fret-diatonic-triads-minor.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-seventh-shapes",
      title: "Seventh chord shapes on the neck",
      estReadMinutes: 7,
      checkFor: [],
    },
    loader: () => import("../content/fret-seventh-shapes.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-octave-shapes",
      title: "Octave shapes — string-skip grips",
      estReadMinutes: 5,
      checkFor: [],
    },
    loader: () => import("../content/fret-octave-shapes.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-wide-octave-shapes",
      title: "Wide octave shapes — the three-string skip",
      estReadMinutes: 5,
      checkFor: [],
    },
    loader: () => import("../content/fret-wide-octave-shapes.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-intervals-standard",
      title: "Interval shapes — perfect-fourth string pairs",
      estReadMinutes: 6,
      checkFor: [],
    },
    loader: () => import("../content/fret-intervals-standard.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-intervals-gb-offset",
      title: "Interval shapes — the G–B string offset",
      estReadMinutes: 5,
      checkFor: [],
    },
    loader: () => import("../content/fret-intervals-gb-offset.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-skip1-intervals",
      title: "Skip-a-string shapes — m7 and M6 grips",
      estReadMinutes: 5,
      checkFor: [],
    },
    loader: () => import("../content/fret-skip1-intervals.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-triad-shapes",
      title: "Triad shapes by string set",
      estReadMinutes: 6,
      checkFor: [],
    },
    loader: () => import("../content/fret-triad-shapes.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-triad-inversions",
      title: "Triad inversions across the neck",
      estReadMinutes: 6,
      checkFor: [],
    },
    loader: () => import("../content/fret-triad-inversions.mdx"),
  },
  {
    metadata: {
      nodeId: "fret-scale-degrees-minor",
      title: "Scale degrees on the neck (minor)",
      estReadMinutes: 5,
      checkFor: [],
    },
    loader: () => import("../content/fret-scale-degrees-minor.mdx"),
  },
];

/** Snapshot of every authored lesson's metadata. Used by `listLessons()`. */
export function lessonRegistry(): ReadonlyArray<LessonRegistryEntry> {
  return ENTRIES;
}

/**
 * Coerce a `.mdx` module's namespace into a typed `LessonModule` and
 * verify its front-matter matches the registry's metadata. Authoring
 * drift (typo in front-matter, mismatched checkFor) fails loudly here
 * rather than producing an undefined-laden card on screen.
 *
 * `remark-mdx-frontmatter` exposes the parsed YAML as a single
 * `frontmatter` named export and the MDX component as `default`.
 */
export function asLessonModule(
  mod: unknown,
  metadata: LessonMetadata,
): LessonModule {
  if (!mod || typeof mod !== "object") {
    throw new Error(`Invalid lesson module for ${metadata.nodeId}: not an object`);
  }
  const m = mod as Record<string, unknown>;
  const def = m["default"];
  if (typeof def !== "function") {
    throw new Error(
      `Invalid lesson module for ${metadata.nodeId}: missing default export`,
    );
  }
  return {
    ...metadata,
    default: def as LessonModule["default"],
  };
}
